import { alpha, ColorSystemOptions } from "@mui/material";
import { shuttlerockBlue, gradient, grey, green, red, amber, purple } from "./darkColors";
import {
  sr25Gray,
  sr25Red,
  sr25Amber,
  sr25Green,
  sr25Teal,
  sr25Blue,
  sr25Purple,
  sr25Violet,
  sr25Pink,
} from "./sr25Colors";

const bg = {
  900: sr25Gray[990],
  800: sr25Gray[980],
  700: sr25Gray[950],
  600: sr25Gray[900],
  500: sr25Gray[850],
  400: sr25Gray[800],
  300: sr25Gray[700],
  200: sr25Gray[600],
  100: sr25Gray[500],
  50: sr25Gray[400],

  A900: alpha(sr25Gray[990], 0.9),
  A800: alpha(sr25Gray[990], 0.8),
  A700: alpha(sr25Gray[990], 0.7),
  A600: alpha(sr25Gray[990], 0.6),
  A500: alpha(sr25Gray[990], 0.5),
  A400: alpha(sr25Gray[990], 0.4),
  A300: alpha(sr25Gray[990], 0.3),
  A200: alpha(sr25Gray[990], 0.2),
  A150: alpha(sr25Gray[990], 0.15),
  A100: alpha(sr25Gray[990], 0.1),
  A50: alpha(sr25Gray[990], 0.05),
  A30: alpha(sr25Gray[990], 0.03),
  A20: alpha(sr25Gray[990], 0.02),
};
const fg = {
  900: sr25Gray[100],
  800: sr25Gray[200],
  700: sr25Gray[300],
  600: sr25Gray[400],
  500: sr25Gray[500],
  400: sr25Gray[600],
  300: sr25Gray[700],
  200: sr25Gray[800],
  100: sr25Gray[900],
  50: sr25Gray[950],

  A900: alpha(sr25Gray[100], 0.9),
  A800: alpha(sr25Gray[100], 0.8),
  A700: alpha(sr25Gray[100], 0.7),
  A600: alpha(sr25Gray[100], 0.6),
  A500: alpha(sr25Gray[100], 0.5),
  A400: alpha(sr25Gray[100], 0.4),
  A300: alpha(sr25Gray[100], 0.3),
  A200: alpha(sr25Gray[100], 0.2),
  A150: alpha(sr25Gray[100], 0.15),
  A100: alpha(sr25Gray[100], 0.1),
  A50: alpha(sr25Gray[100], 0.05),
  A30: alpha(sr25Gray[100], 0.03),
  A20: alpha(sr25Gray[100], 0.02),
};

// `DarkColorScheme` defines the colour palette used in dark mode
export const DarkColorScheme: ColorSystemOptions = {
  palette: {
    mode: "dark",
    // 2025 New DS Colors
    sr25Gray,
    sr25Blue,
    sr25Purple,
    sr25Red,
    sr25Amber,
    sr25Green,
    sr25Teal,
    sr25Violet,
    sr25Pink,
    bg,
    fg,
    surface: {
      main: bg[700],
      a000: alpha(bg[700], 0),
      dim: bg[800],
      receded: bg[800],
      container: bg[900],
      drawer: bg[700],
      elevated: bg[500],
      inset: bg[900],
      inverted: fg[900],
      hover: bg[400],
      dropdown: bg[500],
      overlay: alpha(sr25Gray[100], 0.05), // opacity value matches action.hoverOpacity
      overlayDim: alpha(sr25Gray[100], 0.03),
      overlayDimmer: alpha(sr25Gray[100], 0.02),
      overlayStrong: alpha(sr25Gray[100], 0.1),
      overlayStronger: alpha(sr25Gray[100], 0.2),
      contrastText: fg[900],
      contrastTextDim: fg[700],
      contrastTextDimmer: fg[500],
      contrastTextDimmest: fg[400],
    },
    // Special semantic token group for foreground colors
    // on a surface
    // ex: on.surface, on.primaryContainer
    on: {
      surface: fg[900], // = surface.contrastText
      surfaceDim: fg[700], // = surface.contrastTextDim
      surfaceDimmer: fg[500], // = surface.contrastTextDimmer
      surfaceDimmest: fg[400], // = surface.contrastTextDimmest
      surfaceInverted: bg[800],
      primary: sr25Blue[50], // = primary.contrastText
      secondary: sr25Gray[50], // = secondary.contrastText
      tertiary: sr25Purple[50], // = tertiary.contrastText
    },
    primary: {
      main: sr25Blue[500],
      light: sr25Blue[400],
      dark: sr25Blue[600],
      contrastText: sr25Blue[100],
      container: alpha(sr25Blue[600], 0.4),
      // deprecated
      hover: shuttlerockBlue[300],
      active: shuttlerockBlue[300],
    },
    secondary: {
      main: sr25Gray[700],
      light: sr25Gray[600],
      dark: sr25Gray[800],
      contrastText: sr25Gray[100],
      container: alpha(sr25Gray[700], 0.3),
    },
    tertiary: {
      main: sr25Purple[800],
      light: sr25Purple[700],
      dark: sr25Purple[900],
      contrastText: sr25Purple[100],
      container: alpha(sr25Purple[500], 0.2),
    },
    error: {
      light: red[400],
      main: red[500],
      dark: red[600],
      contrastText: grey[50],
      foreground: sr25Red[500],
    },
    info: {
      main: shuttlerockBlue[400],
      light: shuttlerockBlue[100],
      dark: shuttlerockBlue[700],
      contrastText: grey[900],
      // deprecated
      background: shuttlerockBlue[50],
    },
    success: {
      main: green[500],
      light: green[300],
      dark: green[600],
      border: sr25Green[300],
      container: alpha(sr25Green[600], 0.4),
      contrastText: grey[50],
    },
    warning: {
      main: amber[500],
      light: amber[400],
      dark: amber[600],
      contrastText: grey[50],
      container: alpha(sr25Amber[600], 0.4),
      // deprecated
      active: amber[300],
      background: amber[400],
    },
    background: {
      default: grey[50],
      paper: grey[100],
      secondary: grey[200],
      card: grey.A100,
      cardHover: grey.A200,
      cardAlt: grey.A200,
      cardAltHover: grey.A300,
    },
    foreground: {
      main: grey[200],
      secondary: grey[100],
      tertiary: grey[300],
      hover: grey.A200,
    },
    text: {
      primary: fg[900], // on.surface
      primaryAlt: fg[700], // on.surfaceDim
      secondary: fg[500], // on.surfaceDimmer
      disabled: fg[400], // on.surfaceDimmest
    },
    stroke: {
      main: sr25Gray[700],
      hover: sr25Gray[600],
      active: sr25Blue[500],
      strong: sr25Gray[500],
      transparent: alpha(sr25Gray[100], 0.2),
      transparentDim: alpha(sr25Gray[100], 0.1),
      transparentStrong: alpha(sr25Gray[100], 0.3),
    },
    divider: sr25Gray[700],
    gradient: {
      main: gradient.main,
      light: gradient.hover,
      background: gradient.background,
      blueToPurple: gradient.blueToPurple,
      purpleToBlue: gradient.purpleToBlue,
      pinkToOrange: gradient.pinkToOrange,
      contrastText: "#fff",
      blackToTransparent: gradient.blackToTransparent,
    },
    automation: {
      light: alpha(sr25Purple[500], 0.2),
      main: purple[500],
      dark: purple[700],
      contrastText: grey[50],
    },
    instructions: {
      light: grey[300],
      main: grey[200],
      dark: grey[100],
      contrastText: purple[900],
    },
    action: {
      hover: fg.A50,
      active: fg[700],
      disabled: fg[400],
      disabledBackground: fg.A50,
      hoverOpacity: 0.05,
    },
    gender: {
      all: sr25Green[500],
      female: sr25Purple[500],
      male: sr25Blue[500],
    },
    brand: {
      creativex: "#ff425b",
    },
    grey,
    selection: alpha(sr25Blue[500], 0.3),
    Alert: {
      errorStandardBg: red[400],
      errorStandardColor: grey[100],
      errorStandardIconColor: grey[100],

      infoStandardBg: shuttlerockBlue[100],
      infoStandardColor: shuttlerockBlue[700],
      infoStandardIconColor: shuttlerockBlue[700],

      successStandardBg: green[400],
      successStandardColor: grey[100],
      successStandardIconColor: grey[100],

      warningStandardBg: amber[400],
      warningStandardColor: grey[100],
      warningStandardIconColor: grey[100],

      secondaryStandardBg: grey[300],
      secondaryStandardColor: grey[900],
      secondaryStandardTitleColor: grey[900],
      secondaryStandardIconColor: grey[900],

      automationStandardBg: purple[300],
      automationStandardColor: purple[700],
      automationStandardTitleColor: purple[700],
      automationStandardIconColor: purple[700],

      // applying the opacity on the background all while using a hacky css to apply gradient on boarder reduced our options greatly, hence the use of hsl.
      instructionsStandardBg: `linear-gradient(100.23deg, hsl(209, 100%, 84%) -0.53%, hsl(271, 100%, 81%) 70.81%, hsl(306, 100%, 86%) 115.3%) padding-box, ${gradient.blueToPurple} border-box`,
      instructionsStandardBorder: "2px solid transparent",
      instructionsStandardColor: grey[100],
      instructionsStandardActionColor: grey[100],
      instructionsStandardIconColor: grey[100],

      warningFilledColor: grey[50],
      automationFilledBg: purple[200],
      automationFilledColor: purple[700],
      secondaryFilledBg: grey.A500,
      secondaryFilledColor: grey.A50,
      instructionsFilledBg: gradient.blueToPurple,
      instructionsFilledColor: grey.A900,

      infoOutlinedBg: "transparent",
      infoOutlinedBorder: shuttlerockBlue[100],
      infoOutlinedColor: "#ecf2f9",
      infoOutlinedIconColor: shuttlerockBlue[400],

      successOutlinedBg: "transparent",
      successOutlinedBorder: sr25Green[400],
      successOutlinedColor: sr25Green[50],
      successOutlinedIconColor: sr25Green[400],

      warningOutlinedBg: "transparent",
      warningOutlinedBorder: amber[400],
      warningOutlinedColor: "#f2dbb6",
      warningOutlinedIconColor: amber[500],

      secondaryOutlinedBorder: grey[300],
      secondaryOutlinedColor: "#b8b8b8",
      secondaryOutlinedIconColor: grey[200],

      automationOutlinedBorder: purple[300],
      automationOutlinedColor: "#ede8f9",
      automationOutlinedIconColor: purple[500],

      instructionsOutlinedBg: `linear-gradient(#222222, #222222) padding-box, ${gradient.blueToPurple} border-box`,
      instructionsOutlinedBorder: "2px solid transparent",
      instructionsOutlinedColor: grey.A800,
      instructionsOutlinedActionColor: shuttlerockBlue[500],
      instructionsOutlinedIconColor: shuttlerockBlue[500],
    },
    Button: {
      textPrimaryColor: sr25Blue[300],
      textSecondaryColor: fg[700],

      containedPrimaryContrastText: sr25Blue[50],
      containedPrimaryBg: sr25Blue[600],
      containedPrimaryHoverBg: sr25Blue[500],
      containedPrimaryActiveBg: sr25Blue[600],

      containedSecondaryBg: fg.A100,
      containedSecondaryHoverBg: fg.A200,
      containedSecondaryActiveBg: fg.A100,

      containedAutomationContrastText: sr25Purple[50],
      containedAutomationBg: sr25Purple[700],
      containedAutomationHoverBg: sr25Purple[600],
      containedAutomationActiveBg: sr25Purple[700],

      containedOverlayContrastText: sr25Blue[50],
      containedOverlayBg: grey[300],
      containedOverlayHoverBg: grey[400],
      containedOverlayActiveBg: grey[300],
      containedOverlayDisabledContrastText: grey.A400,
      containedOverlayDisabledBg: grey[300],

      containedErrorContrastText: sr25Red[50],
      containedErrorBg: sr25Red[600],
      containedErrorHoverBg: sr25Red[500],
      containedErrorActiveBg: sr25Red[600],

      containedWarningContrastText: sr25Gray[0],
      containedWarningBg: sr25Amber[400],
      containedWarningHoverBg: sr25Amber[300],
      containedWarningActiveBg: sr25Amber[400],

      containedSuccessContrastText: sr25Gray[0],
      containedSuccessBg: sr25Green[500],
      containedSuccessHoverBg: sr25Green[400],
      containedSuccessActiveBg: sr25Green[500],

      containedInheritColor: "inherit",
      containedInheritBg: fg.A100,
      containedInheritHoverBg: fg.A200,
      containedInheritActiveBg: fg.A50,

      outlinedPrimaryHoverColor: sr25Blue[500],
      outlinedPrimaryHoverBg: sr25Blue[300],
      outlinedPrimaryBorder: alpha(sr25Blue[500], 0.5),
      outlinedPrimaryDisabledBorder: alpha(fg[800], 0.2),

      outlinedSecondaryBg: "transparent",
      outlinedSecondaryBorder: alpha(fg[800], 0.3),
      outlinedSecondaryHoverBg: alpha(fg[800], 0.05),
      outlinedSecondaryHoverBorder: alpha(fg[800], 0.5),
      outlinedSecondaryDisabledBorder: alpha(fg[800], 0.2),
    },
    Card: {
      bg: grey.A100,
    },
    Checkbox: {
      color: sr25Blue[50],
      checkedReadonlyColor: shuttlerockBlue[500],
      checkedLabelColor: grey.A900,
    },
    Chip: {
      m3PrimaryColor: sr25Blue[50],
      m3PrimaryHoverBg: sr25Blue[500],
      m3SecondaryColor: fg[700],
      m3SecondaryHoverBg: fg.A100,
      m3DefaultColor: fg[700],
      m3DefaultHoverBg: fg.A100,
      m3ErrorColor: sr25Red[400],
      m3ErrorHoverBg: sr25Red[900],
      m3WarningColor: sr25Amber[400],
      m3WarningHoverBg: sr25Amber[900],
      m3InfoColor: sr25Blue[400],
      m3InfoHoverBg: sr25Blue[900],
      m3SuccessColor: sr25Green[400],
      m3SuccessHoverBg: sr25Green[900],
      m3ClickableBg: "rgba(255, 255, 255, 0.15)",

      filledSuccessColor: grey[50],
      filledSuccessBg: green[500],
      filledWarningColor: grey[50],
      filledWarningBg: amber[500],
      filledErrorColor: grey[50],
      filledErrorBg: red[500],
      filledSecondaryColor: grey[900],
      filledSecondaryBg: grey[200],
      filledDefaultColor: grey.A900,
      filledDefaultBg: grey.A200,
      xsFilledDefaultColor: grey.A900,
      xsFilledDefaultBg: grey.A200,
    },
    Dialog: {
      bg: grey[100],
      color: grey.A600,
    },
    LinearProgress: {
      primaryBarColor: shuttlerockBlue[300],
      primaryColor: grey[300],
      successBarColor: green[500],
      errorBarColor: red[500],
    },
    Menu: {
      bg: bg[500], // surface.dropdown
      color: fg[900], // on.surface
      boxShadow: `0px 5px 15px 0px rgba(0, 0, 0, 0.5), 0px 0px 0px 1px rgba(0, 0, 0, 0.1)`,
    },
    Radio: {
      color: grey.A600,
      checkedReadonlyColor: grey.A900,
      checkedLabelColor: grey.A900,
    },
    Switch: {
      defaultColor: sr25Gray[100],
      defaultDisabledColor: sr25Gray[600],
      primaryDisabledColor: alpha(sr25Blue[500], 0.5),
      secondaryDisabledColor: alpha(sr25Gray[700], 0.5),
      errorDisabledColor: alpha(red[500], 0.5),
      infoDisabledColor: alpha(shuttlerockBlue[400], 0.5),
      successDisabledColor: alpha(green[500], 0.5),
      warningDisabledColor: alpha(amber[500], 0.5),
      thumbColor: sr25Gray[100],
      trackOffBg: fg.A100,
      trackOffBorder: sr25Gray[700],
      trackOffHoverBg: fg.A200,
      trackOffHoverBorder: sr25Gray[600],
      trackOffActiveBg: fg.A300,
      trackOffActiveBorder: sr25Gray[500],
      trackOnPrimaryBg: sr25Blue[600],
      trackOnPrimaryHoverBg: sr25Blue[500],
      trackOnPrimaryActiveBg: sr25Blue[700],
      trackOnSuccessBg: sr25Green[500],
      trackOnSuccessHoverBg: sr25Green[400],
      trackOnSuccessActiveBg: sr25Green[600],
      focusRingColor: alpha(sr25Blue[500], 0.3),
      focusRingWidth: "3px",
    },
    Select: {
      color: grey.A900,
    },
    FormLabel: {
      color: grey.A600,
    },
    FilledInput: {
      color: fg[900],
      bg: alpha(sr25Gray[990], 0.3),
      hoverBg: alpha(sr25Gray[990], 0.5),
      disabledBg: alpha(sr25Gray[990], 0.2),
      placeholderColor: fg[500],
      placeholderOpacity: 0.5,
    },
    OutlinedInput: {
      color: fg[900],
      bg: bg[800],
      disabledBg: bg[700],
      disabledBorder: sr25Gray[700],
      border: sr25Gray[700], // stroke.main
      hoverBorder: sr25Gray[600], // stroke.hover
      focusBorder: sr25Blue[500], // stroke.active
      boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.024)",
      placeholderColor: fg[500],
      placeholderOpacity: 0.5,
    },
    Slider: {
      bg: grey.A600,
      primaryTrack: shuttlerockBlue[400],
      railColor: grey.A300,
      markBg: grey.A200,
      valueLabelBg: grey[400],
    },
    SvgIcon: {
      primaryColor: shuttlerockBlue[500],
      successColor: green[500],
      errorColor: red[500],
      automationColor: purple[700],
    },
    Tabs: {
      trackColor: grey[50],
    },
    Tab: {
      color: grey[800],
      hoverBg: alpha(sr25Gray[100], 0.1), // overlayStrong
      activeBg: alpha(sr25Gray[100], 0.2), // overlayStronger
      selectedBg: alpha(sr25Gray[100], 0.2), // overlayStronger
      selectedColor: grey[800],
    },
    Table: {
      rowHoverBg: grey[200],
      headBg: grey[100],
      bodyColor: grey.A900,
      border: sr25Gray[700],
    },
    TableCell: {
      border: sr25Gray[800],
    },
    Tooltip: {
      bg: grey[700],
      color: grey[100],
      boxShadow: "none",
    },
  },
};
